import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Map from '../../../components/Map'
import locationsData from '../../../../src/data/locations.json'
import MyButton from '../../../components/buttons'
import { FaTrash } from 'react-icons/fa'
import { Checkbox, FormControlLabel } from '@mui/material'
import CookieManager from '../../../lib/CookiesManager'

interface JobOfferLocationProps {
  formData: any
  handleFieldChange: (key: string, value: any) => void
}

interface FormData {
  move: boolean
  location: string[]
  [key: string]: any
}

const JobOfferLocationForm = ({
  formData: initialFormData,
}: JobOfferLocationProps) => {
  const { t } = useTranslation()
  const [updateComponent, setUpdateComponent] = useState(false)

  const getStorageKey = () => {
    const currentPath = window.location.pathname
    const basePath = currentPath.split('?')[0].split('/').filter(Boolean)[1] as
      | 'announce-work'
      | 'announce-event'
      | 'announce-establishment'
      | undefined

    const keyMap: { [key: string]: string } = {
      'announce-work': 'myJobOffer',
      'announce-event': 'myEvent',
      'announce-establishment': 'myEstablishment',
    }

    return basePath ? keyMap[basePath] : 'myJobOffer'
  }

  const storageKey = getStorageKey()

  const [formData, setFormData] = useState<FormData>(() => {
    const savedFormData = JSON.parse(localStorage.getItem(storageKey) || '{}')
    return {
      ...initialFormData,
      ...savedFormData,
      location: savedFormData.location || [],
      move: true,
    }
  })

  const updateLocalStorage = (updates: Partial<FormData>) => {
    const currentData = JSON.parse(localStorage.getItem(storageKey) || '{}')
    const newData = { ...currentData, ...updates }
    localStorage.setItem(storageKey, JSON.stringify(newData))
  }

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === storageKey) {
        const newData = JSON.parse(event.newValue || '{}')
        setFormData(prev => ({
          ...prev,
          ...newData,
          location: newData.location || [],
        }))
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [storageKey])

  useEffect(() => {
    const userId = CookieManager.getUserId()
    if (userId) {
      const currentData = JSON.parse(localStorage.getItem(storageKey) || '{}')
      updateLocalStorage({ ...currentData, userId })
    }
  }, [])

  const handleProvinceClick = (id: string) => {
    setFormData(prevFormData => {
      const updatedLocations = prevFormData.location.includes(id)
        ? prevFormData.location.filter(loc => loc !== id)
        : [...prevFormData.location, id]

      updateLocalStorage({ location: updatedLocations })

      return {
        ...prevFormData,
        location: updatedLocations,
      }
    })
  }

  const handleRemoveField = (event: React.MouseEvent) => {
    event.preventDefault()
    setFormData(prev => ({ ...prev, location: [] }))
    updateLocalStorage({ location: [] })
    setUpdateComponent(prev => !prev)
  }

  const handleSelectAllClick = () => {
    const allProvinces = Object.keys(locationsData.location)
    const newLocations =
      formData.location.length === allProvinces.length ? [] : allProvinces

    setFormData(prev => ({ ...prev, location: newLocations }))
    updateLocalStorage({ location: newLocations })
  }

  const renderLocations = () => {
    return (
      <div className='flex flex-col sm:flex-wrap sm:flex-row'>
        {formData.location
          .filter(locationKey => locationKey !== 'noEscort')
          .map((locationKey, index) => (
            <div
              onClick={() => handleRemoveProvince(locationKey)}
              className='rounded-lg cursor-pointer relative group 
                  hover:bg-redpink hover:text-white mb-2 sm:mb-0 
                  px-4 py-2 text-center w-full sm:w-1/2'
              key={`${locationKey}-${index}`}
            >
              {t(`provinces.${locationKey}`)}
              <FaTrash
                className='absolute top-1/2 right-2 transform -translate-y-1/2 
                     opacity-0 group-hover:opacity-100 text-white'
              />
            </div>
          ))}
      </div>
    )
  }

  const handleRemoveProvince = (locationKey: string) => {
    const updatedLocations = formData.location.filter(
      loc => loc !== locationKey,
    )
    setFormData(prev => ({ ...prev, location: updatedLocations }))
    updateLocalStorage({ location: updatedLocations })
  }

  return (
    <div className='flex flex-col lg:flex-row'>
      <div className='w-full mb-2 lg:mb-0 sm:mr-2'>
        <Map
          selectedProvinces={formData.location}
          setSelectedProvinces={renderLocations}
          onProvinceClick={handleProvinceClick}
        />
      </div>
      <div className='field-container bg-grisclair p-2 rounded-lg w-full text-center'>
        <div className='mb-4 p-4'>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  formData.location.length ===
                  Object.keys(locationsData.location).length
                }
                onChange={handleSelectAllClick}
                className='form-checkbox h-5 w-5 text-redpink'
              />
            }
            label={
              <span className='cursor-pointer text-anthracite'>
                {t('create-ad.select-all')}
              </span>
            }
            className='flex items-center space-x-2'
          />
        </div>
        {renderLocations()}
        {formData.location.length > 0 && (
          <MyButton
            variant='redpink-anthracite'
            className='mt-2'
            onClick={handleRemoveField}
          >
            {t('create-ad.delete-all')}
          </MyButton>
        )}
      </div>
    </div>
  )
}

export default JobOfferLocationForm
